/**
 * RTWO Event Bus — Event Registry Service
 */

import { EVENT_BUS_SERVICE_IDS } from '../domain-models';
import type {
  EventId,
  RTWOEvent,
  RTWOEventPriority,
  RTWOEventStatus,
  RTWOEventType,
} from '../domain-models';
import type { EventRegistryResult, IEventRegistryService } from '../service-interfaces';
import type { EBusRepositories } from '../repositories/ebus-repositories';
import type { EngineSourceAdapterRegistry } from '../registry/adapter-registries';
import { EBusInternalEventTypes } from '../events/ebus-internal-event-bus';
import { BaseEBusService, type EBusServiceContext } from './base-ebus-service';

export class EventRegistryService extends BaseEBusService implements IEventRegistryService {
  readonly serviceId = EVENT_BUS_SERVICE_IDS.EVENT_REGISTRY;
  readonly serviceName = 'Event Registry';
  readonly description =
    'Central registry for all RTWO events via engine source adapter ingestion.';
  readonly integrations = ['Core Engine', 'All RTWO Engines', 'System Registry'];
  readonly requiresRealEventSources = true;

  constructor(
    context: EBusServiceContext,
    private readonly repositories: EBusRepositories,
    private readonly engineSourceRegistry: EngineSourceAdapterRegistry
  ) {
    super(context);
  }

  async registerEvent(
    eventType: RTWOEventType,
    sourceModule: string,
    priority: RTWOEventPriority
  ): Promise<EventRegistryResult> {
    if (!this.engineSourceRegistry.isConfigured()) {
      return {
        success: false,
        eventId: null,
        error:
          'Waiting for Real Event Sources — No events can be registered until real RTWO engine event sources are connected.',
        isPlaceholder: true,
        source: 'Event Bus — Waiting for Real Event Sources',
      };
    }

    const adapterResult = await this.engineSourceRegistry
      .getActiveAdapter()
      .publishEvent(eventType, sourceModule, null, null);

    if (adapterResult?.event) {
      this.repositories.events.save(adapterResult.event);
      this.context.eventBus.emit(EBusInternalEventTypes.EVENT_REGISTERED, {
        eventId: adapterResult.event.eventId,
        eventType,
        sourceModule,
        priority,
      });
    }

    return {
      success: false,
      eventId: null,
      error:
        'Waiting for Real Event Sources — No events can be registered until real RTWO engine event sources are connected.',
      isPlaceholder: true,
      source: 'Event Bus — Waiting for Real Event Sources',
    };
  }

  getAllEvents(): RTWOEvent[] {
    if (!this.engineSourceRegistry.isConfigured()) {
      return [];
    }

    return this.repositories.events.findAll();
  }

  getEvent(eventId: EventId): RTWOEvent | null {
    if (!this.engineSourceRegistry.isConfigured()) {
      return null;
    }

    return this.repositories.events.findById(eventId);
  }

  getEventsByStatus(status: RTWOEventStatus): RTWOEvent[] {
    if (!this.engineSourceRegistry.isConfigured()) {
      return [];
    }

    return this.repositories.events.findByStatus(status);
  }

  getEventCount(): 0 {
    return 0;
  }
}
